// Global filter state — the three axes (activity / region / brand) plus the
// trailing date window. Persisted to localStorage and exposed through context so
// every route reads the same selection. Query keys include the filter values so
// TanStack Query refetches when they change.
import { createContext, useContext, useState, useCallback, useMemo } from "react";
import type { FilterState, ActivityFilter, RegionFilter, BrandFilter, ChannelFilter, CurrencyCode } from "@/lib/api/types";

const STORAGE_KEY = "prado_pulse.filters";

const DEFAULT: FilterState = { activity: "all", region: "all", brand: "all", channel: "all", currency: "EUR", days: 30 };

function load(): FilterState {
  if (typeof window === "undefined") return DEFAULT;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT;
    return { ...DEFAULT, ...(JSON.parse(raw) as Partial<FilterState>) };
  } catch {
    return DEFAULT;
  }
}

interface FiltersContextValue extends FilterState {
  setActivity: (a: ActivityFilter) => void;
  setRegion: (r: RegionFilter) => void;
  setBrand: (b: BrandFilter) => void;
  setChannel: (c: ChannelFilter) => void;
  setCurrency: (c: CurrencyCode) => void;
  setDays: (d: number) => void;
  /** Explicit window; null returns to the trailing `days` window. */
  setRange: (r: { from: string; to: string } | null) => void;
  reset: () => void;
  /** Length of the active window in days — `days`, or the custom range's span.
   *  Anything that scales a monthly target or labels the period must use this,
   *  never `days`, which stays at its last preset while a custom range is active. */
  windowDays: number;
  /** ISO date (yyyy-mm-dd) of the start of the window. */
  fromDate: string;
  /** ISO date (yyyy-mm-dd) today. */
  toDate: string;
}

const FiltersContext = createContext<FiltersContextValue | null>(null);

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

export function FiltersProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<FilterState>(load);

  const persist = useCallback((next: FilterState) => {
    setState(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, []);

  const value = useMemo<FiltersContextValue>(() => {
    // Windows end YESTERDAY, the last complete day. The connectors pull up to
    // yesterday, so including today would silently show N-1 days of data under an
    // "N dagen" label — which made every total read ~1/N too low against the
    // platforms' own reporting.
    const toDate = isoDaysAgo(1);
    const r = state.range;
    const windowDays = r
      ? Math.max(1, Math.round((new Date(r.to).getTime() - new Date(r.from).getTime()) / 86_400_000) + 1)
      : state.days;
    return {
      ...state,
      windowDays,
      // A range saved earlier may reach past yesterday; clamp it like the picker.
      fromDate: r ? (r.from > toDate ? toDate : r.from) : isoDaysAgo(state.days),
      toDate: r ? (r.to > toDate ? toDate : r.to) : toDate,
      setActivity: (activity) => persist({ ...state, activity }),
      setRegion: (region) => persist({ ...state, region }),
      setBrand: (brand) => persist({ ...state, brand }),
      setChannel: (channel) => persist({ ...state, channel }),
      setCurrency: (currency) => persist({ ...state, currency }),
      setDays: (days) => persist({ ...state, days, range: null }),
      setRange: (range) => persist({ ...state, range }),
      reset: () => persist(DEFAULT),
    };
  }, [state, persist]);

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export function useFilters(): FiltersContextValue {
  const ctx = useContext(FiltersContext);
  if (!ctx) throw new Error("useFilters must be used within <FiltersProvider>");
  return ctx;
}

/** Stable key fragment for TanStack Query — changes whenever a filter changes. */
export function filterKey(f: FilterState): string {
  return `${f.activity}|${f.region}|${f.brand}|${f.channel}|${f.range ? `${f.range.from}..${f.range.to}` : f.days}`;
}
